import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { uploadImage } from "../../utils/helpers/fileupload";
import { getSubjectTeacher } from "../../store/slices/subjectteacher";
import { addNote } from "../../store/slices/notes";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";

const getAcademicSession = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // Jan = 0

  // Academic year starts April (month >= 3)
  const startYear = month >= 3 ? year : year - 1;
  const endYear = startYear + 1;

  return `${startYear}-${String(endYear).slice(-2)}`;
};

const requiredMessages = {
  standardId: "Please select standard",
  subjectId: "Please select subject",
  chapter: "Please enter chapter name",
  file: "Please upload a file",
};

export default function AddNotes() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const teacherId = user?.data?.teacherId;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rawData, setRawData] = useState([]);
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const res = await dispatch(getSubjectTeacher({ teacherId })).unwrap();
      const data = res.data || [];

      setRawData(data);

      // unique standards
      const uniqueStandards = [
        ...new Map(
          data.map((item) => [item.standardId._id, item.standardId]),
        ).values(),
      ];

      setStandards(uniqueStandards);
    };

    fetchData();
  }, [dispatch, teacherId]);

  const [formData, setFormData] = useState({
    standardId: "",
    sectionId: "",
    subjectId: "",
    chapter: "",
  });
  const handleStandardChange = (e) => {
    const standardId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      standardId,
      subjectId: "",
    }));

    // Clear validation errors
    setErrors((prev) => ({
      ...prev,
      standardId: "",
      subjectId: "",
    }));

    const filteredSubjects = rawData
      .filter((item) => item.standardId._id === standardId)
      .map((item) => item.subjectId);

    const uniqueSubjects = [
      ...new Map(filteredSubjects.map((sub) => [sub._id, sub])).values(),
    ];

    setSubjects(uniqueSubjects);
  };


  const validateForm = () => {
    let newErrors = {};

    Object.keys(requiredMessages).forEach((key) => {
      if (key === "file") {
        if (!file) newErrors.file = requiredMessages.file;
        return;
      }

      if (!formData[key] || formData[key].toString().trim() === "") {
        newErrors[key] = requiredMessages[key];
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only PDF, JPG, PNG files allowed");
      e.target.value = "";
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File must be under 2MB");
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    setErrors((prev) => ({ ...prev, file: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const uploadedFileUrl = await uploadImage(file, token);


      const payload = {
        standardId: formData.standardId,
        sectionId: formData.sectionId,
        subjectId: formData.subjectId,
        teacherId,
        chapter: formData.chapter,
        session: getAcademicSession(),
        file: uploadedFileUrl, // single file
      };

      console.log("payload addNotes", payload)

      await dispatch(addNote(payload)).unwrap();

      toast.success("Notes added successfully");
      navigate("/teacher/notes");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to add notes",
      );
    }
  };

  return (
    <>

      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/teacher/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/teacher/notes">Notes</Link>

            <FiChevronRight className="separator" />

            <span className="active">Add Note</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Standard</Form.Label>
              <Form.Select
                value={formData.standardId} name="standardId"
                onChange={handleStandardChange}
              >
                <option value="">Select Standard</option>
                {standards.map((std) => (
                  <option key={std._id} value={std._id}>
                    {std.name}
                  </option>
                ))}
              </Form.Select>

              {errors.standardId && (
                <small className="text-danger">{errors.standardId}</small>
              )}
            </Col>

            {/* Section */}

            {/* Subject */}
            <Col md={4}>
              <Form.Label>Subject</Form.Label>
              <Form.Select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
                disabled={!subjects.length}
              >
                <option value="">Select Subject</option>
                {subjects.map((sub) => (
                  <option key={sub._id} value={sub._id}>
                    {sub.name}
                  </option>
                ))}
              </Form.Select>

              {errors.subjectId && (
                <small className="text-danger">{errors.subjectId}</small>
              )}
            </Col>

            <Col md={4}>
              <Form.Label>Chapter</Form.Label>
              <Form.Control
                name="chapter"
                value={formData.chapter}
                onChange={handleChange}
                placeholder="Enter chapter name"
              />
              {errors.chapter && (
                <small className="text-danger">{errors.chapter}</small>
              )}
            </Col>

            {/* File Upload */}
            <Col md={6}>
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />

              <small className="text-muted">
                Only PDF / JPG / PNG allowed. Max size: 10MB.
              </small>
              {errors.file && (
                <div>
                  <small className="text-danger">{errors.file}</small>
                </div>
              )}
            </Col>
          </Row>

          <div className="an-actions mt-5">
            <button
              type="button"
              className="btn-cancel"
              style={{ color: "black" }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Submit
            </button>
          </div>
        </Form>
      </div>
    </>
  );
}
